import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';

import * as selectors from '../../../multiLoanDashboard/accountsSelector';

import DashboardLoanHeader from '../../../sharedDashboardComponents/DashboardLoanHeader';
import DashboardActions from './dashboardActions/DashboardActions';

import messages from './OutstandingView.messages';
import styles from './OutstandingView.module.css';
import { VariableRate } from '../../../ui/rate/Rate';
import LoanProgressBar from '../../../sharedDashboardComponents/LoanProgressBar/LoanProgressBar';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals/MoneyWithDecimals';
import { Tooltip } from '../../../ui/tooltip/Tooltip';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import { TooltipQuestionMarkSVG } from '../../../../svgComponents/tooltipQuestionMarkSVG/TooltipQuestionMarkSVG';
import FAQs from '../../../sharedDashboardComponents/FAQs/FAQs';
import questions from './OutstandingView.faqs';
import { useKeenSlider } from 'keen-slider/react';
import { Button, Icon } from '@iwoca/orion';
import RblTopUpForm from './rblTopUpForm/rblTopUpForm';
import {
    TOP_UP_ACTION,
    TOP_UP_DRAWDOWN_ACTION
} from '../../../sharedDashboardComponents/LoanProgressBar/DashboardActions.types.ts';
import CustomiseOffer from '../../../myOffers/approvedView/rblApprovedView/CustomiseOffer';
import { tracking } from '@iwoca/frontend-tracking-library';

const mapStateToProps = (state) => {
    const loanAmountAtLoanCreation = selectors.getDrawnDownLoanAmount(state);
    const feesAtLoanCreation =
        selectors.getRevenueFeeAmountAtLoanCreation(state);
    const leftToPay = selectors.getOutstandingTotal(state);
    const totalToPay = loanAmountAtLoanCreation + feesAtLoanCreation;
    const totalRepaid = totalToPay - leftToPay;
    return {
        loanAmount: loanAmountAtLoanCreation,
        fees: feesAtLoanCreation,
        repaid: totalRepaid,
        totalToRepay: totalToPay,
        sweepFraction: selectors.getRevenueSweepFraction(state),
        leftToPay: leftToPay,
        estimatedTimeToRepay:
            selectors.getRevenueEstimatedTimeToRepayInMonths(state),
        maxApprovedAmount: selectors.getCreditLimit(state),
        drawDownDate: selectors.getLatestFundingCashflowDate(state),
        revenueStreamTags: selectors.getRevenueStreamTagsFromAccounts(state)
    };
};

const OutstandingView = ({
    loanAmount,
    fees,
    repaid,
    totalToRepay,
    sweepFraction,
    leftToPay,
    estimatedTimeToRepay,
    maxApprovedAmount,
    drawDownDate,
    revenueStreamTags,
    accountId,
    productType
}) => {
    useEffect(() => {
        tracking.ampli.dashboardViewed({
            account_id: accountId,
            product_type: productType,
            dashboard_state: 'ongoing'
        });
    }, [accountId, productType]);

    const [activeDashboardAction, setActiveDashboardAction] = useState(null);
    const [sliderRef, slider] = useKeenSlider({
        initial: 0,
        drag: false // disables phone controls and gestures
    });

    function unsetActiveDashboardAction() {
        setActiveDashboardAction(null);
    }

    return (
        <div className={styles.dashboardContainerWithFAQs}>
            <DashboardLoanHeader
                productType={selectors.REVENUE_BASED_LOAN_PRODUCT_TYPE}
                productInfo={
                    sweepFraction && (
                        <span data-testid="sweep-info">
                            <FormattedMessage
                                {...messages.sweepInfo}
                                values={{
                                    sweep: (
                                        <VariableRate
                                            value={sweepFraction}
                                            maximumFractionDigits={2}
                                            minimumFractionDigits={0}
                                        />
                                    )
                                }}
                            />
                            {getCountry() === 'UK' &&
                                revenueStreamTags &&
                                revenueStreamTags.length > 0 && (
                                    <Tooltip
                                        message={
                                            <>
                                                <span
                                                    className={
                                                        styles.toolTipMessageLineOne
                                                    }
                                                >
                                                    Eligible Sales:
                                                </span>
                                                {revenueStreamTags.map(
                                                    (tag) => {
                                                        return (
                                                            <span
                                                                key={tag}
                                                                className={
                                                                    styles.toolTipMessageLineTwo
                                                                }
                                                            >
                                                                {tag}
                                                            </span>
                                                        );
                                                    }
                                                )}
                                            </>
                                        }
                                    >
                                        <TooltipQuestionMarkSVG
                                            className={`${styles.icon} ${styles.questionIcon} ${styles.headerTooltipIcon}`}
                                        />
                                    </Tooltip>
                                )}
                        </span>
                    )
                }
            />
            <div className={styles.loanInformationWrapper}>
                <div className={styles.loanInformation}>
                    <div className={styles.progressBarContainer}>
                        <div className={styles.LeftToPayContainer}>
                            <div className={styles.LeftToPayLabel}>
                                <FormattedMessage
                                    {...messages.leftToPayLabel}
                                />
                            </div>
                            <span data-testid="total-amount-left-to-pay">
                                <MoneyWithDecimals
                                    className={styles.leftToPayValue}
                                    value={leftToPay}
                                />
                            </span>
                        </div>

                        {leftToPay > 0 && (
                            <>
                                <div
                                    className={
                                        styles.estimatedTimeToRepayContainer
                                    }
                                >
                                    <div
                                        className={styles.loanInformationLabel}
                                    >
                                        <FormattedMessage
                                            {...messages.estimatedTimeToRepayLabel}
                                        />
                                    </div>

                                    <span
                                        data-testid="estimated-time-to-repay"
                                        className={styles.loanInformationValue}
                                    >
                                        {estimatedTimeToRepay === 1 ? (
                                            <FormattedMessage
                                                {...messages.lastMonth}
                                            />
                                        ) : (
                                            <FormattedMessage
                                                {...messages.months}
                                                values={{
                                                    estimatedTimeToRepay
                                                }}
                                            />
                                        )}
                                    </span>
                                </div>

                                <div
                                    className={
                                        styles.progressBarLabelsContainer
                                    }
                                >
                                    <div data-testid="repaid">
                                        <div
                                            className={
                                                styles.loanInformationLabel
                                            }
                                        >
                                            <FormattedMessage
                                                {...messages.repaidLabel}
                                            />
                                        </div>
                                        <MoneyWithDecimals
                                            className={
                                                styles.loanInformationValue
                                            }
                                            value={repaid}
                                        />
                                    </div>

                                    <div>
                                        <div
                                            className={
                                                styles.loanInformationLabel
                                            }
                                        >
                                            <span data-testid="outstanding-tooltip">
                                                <Tooltip
                                                    message={
                                                        <FormattedMessage
                                                            {...messages.tooltip}
                                                            values={{
                                                                drawDownDate: (
                                                                    <FormattedDate
                                                                        value={
                                                                            new Date(
                                                                                drawDownDate
                                                                            )
                                                                        }
                                                                        day="2-digit"
                                                                        month="short"
                                                                        year="numeric"
                                                                    />
                                                                ),
                                                                maxApprovedAmount:
                                                                    (
                                                                        <MoneyNoDecimals
                                                                            className={
                                                                                styles.maxApprovedAmount
                                                                            }
                                                                            value={
                                                                                maxApprovedAmount
                                                                            }
                                                                        />
                                                                    ),
                                                                loanAmount: (
                                                                    <MoneyNoDecimals
                                                                        className={
                                                                            styles.loanAmount
                                                                        }
                                                                        value={
                                                                            loanAmount
                                                                        }
                                                                    />
                                                                ),
                                                                fees: (
                                                                    <MoneyNoDecimals
                                                                        className={
                                                                            styles.fees
                                                                        }
                                                                        value={
                                                                            fees
                                                                        }
                                                                    />
                                                                )
                                                            }}
                                                        />
                                                    }
                                                >
                                                    <TooltipQuestionMarkSVG
                                                        className={`${styles.icon} ${styles.questionIcon}`}
                                                    />
                                                </Tooltip>
                                            </span>
                                            <FormattedMessage
                                                {...messages.totalToRepayLabel}
                                            />
                                        </div>
                                        <span data-testid="total-loan-amount">
                                            <MoneyWithDecimals
                                                className={
                                                    styles.loanInformationValue
                                                }
                                                value={totalToRepay}
                                            />
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}

                        <LoanProgressBar
                            productType={
                                selectors.REVENUE_BASED_LOAN_PRODUCT_TYPE
                            }
                        />
                    </div>

                    <div ref={sliderRef} className="keen-slider">
                        <div className="keen-slider__slide">
                            <DashboardActions
                                accountId={accountId}
                                setActiveDashboardAction={(action) => {
                                    setActiveDashboardAction(action);
                                    slider.current?.next();
                                }}
                            />
                        </div>

                        <div className="keen-slider__slide">
                            {activeDashboardAction ===
                            TOP_UP_DRAWDOWN_ACTION ? (
                                <div>
                                    <Button
                                        className={styles.backButton}
                                        variant="tertiary"
                                        size="small"
                                        onClick={() => {
                                            slider.current?.prev();
                                            unsetActiveDashboardAction();
                                        }}
                                    >
                                        <Icon icon="angleLeft" />
                                        <FormattedMessage
                                            {...messages.decideLater}
                                        />
                                    </Button>
                                    <CustomiseOffer />
                                </div>
                            ) : (
                                activeDashboardAction === TOP_UP_ACTION && (
                                    <div>
                                        <Button
                                            className={styles.backButton}
                                            variant="tertiary"
                                            size="small"
                                            onClick={() => {
                                                slider.current?.prev();
                                                unsetActiveDashboardAction();
                                            }}
                                        >
                                            <Icon icon="angleLeft" />
                                            <FormattedMessage
                                                {...messages.decideLater}
                                            />
                                        </Button>
                                        <RblTopUpForm />
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
                {getCountry() === 'DE' && <FAQs questions={questions()} />}
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(OutstandingView);
